import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleFilterOptions, EnabledStates, SelectedValues } from '@/api';
import { BundleType, BundleTypeKeys, type RootState } from '@/store';

interface BundleFilterOptionsState {
  bundleFilterOptions: BundleFilterOptions[];
  enabledStates: EnabledStates;
  selectedValues: SelectedValues;
  // Remembers the end of the most recent non-empty date range so the calendar can
  // re-open on that month even after the range has been cleared. Stored as an ISO
  // string (Date objects aren't safe to put in Redux state). Scoped per BundleType
  // and cleared on case/bundle change (via setBundleFilterOptions) so it can't
  // leak across cases or bundles.
  lastAppliedDateEnd: string | null;
}

const initialState: BundleFilterOptionsState = {
  bundleFilterOptions: [],
  enabledStates: {},
  selectedValues: {},
  lastAppliedDateEnd: null,
};

const bundleFilterOptionsSlice = createSlice({
  name: 'bundleFilterOptions',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState,
  } as Record<BundleTypeKeys, BundleFilterOptionsState>,
  reducers: {
    setBundleFilterOptions: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleFilterOptions[];
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].bundleFilterOptions = data;
      // New options = new case/bundle context; drop the remembered date.
      state[key].lastAppliedDateEnd = null;
    },
    setEnabledStates: (state, action: PayloadAction<{ key: BundleTypeKeys; data: EnabledStates }>) => {
      const { key, data } = action.payload;
      state[key].enabledStates = data;
    },
    setSelectedValues: (state, action: PayloadAction<{ key: BundleTypeKeys; data: SelectedValues }>) => {
      const { key, data } = action.payload;
      state[key].selectedValues = data;
    },
    setLastAppliedDateEnd: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string | null }>) => {
      const { key, data } = action.payload;
      state[key].lastAppliedDateEnd = data;
    },
  },
});

export const { setBundleFilterOptions, setEnabledStates, setSelectedValues, setLastAppliedDateEnd } =
  bundleFilterOptionsSlice.actions;

export const selectBundleFilterOptions = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleFilterOptions[activeBundleType].bundleFilterOptions
    : initialState.bundleFilterOptions;
};

export const selectEnabledStates = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleFilterOptions[activeBundleType].enabledStates : initialState.enabledStates;
};

export const selectSelectedValues = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleFilterOptions[activeBundleType].selectedValues : initialState.selectedValues;
};

export const selectLastAppliedDateEnd = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleFilterOptions[activeBundleType].lastAppliedDateEnd
    : initialState.lastAppliedDateEnd;
};

export const bundleFilterOptionsReducer = bundleFilterOptionsSlice.reducer;
